import type { Activity } from '../types/activity';

export type InsightBasis = 'delivery' | 'all';
export type InsightFilter = 'all' | 'open' | 'attention' | 'upcoming' | 'undated' | 'ongoing' | Activity['status'];
const DAY = 86400000;
/** Date-only arithmetic avoids UTC/local midnight and DST boundary drift. */
export function dateDay(value: string | null): number | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const time = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(time) && new Date(time).toISOString().slice(0, 10) === value ? time / DAY : null;
}
export function localToday(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}
export function addDateDays(value: string, days: number): string {
  const day = dateDay(value);
  if (day === null) throw new Error('Invalid date window');
  return new Date((day + days) * DAY).toISOString().slice(0, 10);
}
export function overdueAge(activity: Activity, today: string): number {
  const due = dateDay(activity.dueDate), day = dateDay(today);
  return activity.status !== 'done' && activity.scheduleMode === 'dated' && due !== null && day !== null ? Math.max(0, day - due) : 0;
}
export function countableActivities(activities: Activity[], basis: InsightBasis): Activity[] {
  const parents = new Set(activities.map(activity => activity.parentId).filter(Boolean));
  return basis === 'all' ? activities : activities.filter(activity => !parents.has(activity.id));
}
export function isUpcoming(activity: Activity, today: string): boolean {
  const due = dateDay(activity.dueDate), day = dateDay(today);
  return activity.status !== 'done' && activity.scheduleMode === 'dated' && due !== null && day !== null && due >= day && due < day + 28;
}
export function matchesInsight(activity: Activity, activities: Activity[], params: URLSearchParams, today: string, parentIds?: ReadonlySet<string>): boolean {
  if (params.get('basis') === 'delivery' && (parentIds ? parentIds.has(activity.id) : activities.some(item => item.parentId === activity.id))) return false;
  if (params.get('program') && activity.owningUnit !== params.get('program')) return false;
  if (params.get('region') && activity.region !== params.get('region')) return false;
  if (params.get('activity') && activity.id !== params.get('activity')) return false;
  const age = overdueAge(activity, today);
  if (params.get('age') === '1-7' && !(age >= 1 && age <= 7)) return false;
  if (params.get('age') === '8-30' && !(age >= 8 && age <= 30)) return false;
  if (params.get('age') === '31+' && age < 31) return false;
  switch (params.get('insight')) {
    case 'open': return activity.status !== 'done';
    case 'attention': return activity.status === 'blocked' || age > 0;
    case 'upcoming': return isUpcoming(activity, today);
    case 'undated': return activity.status !== 'done' && activity.scheduleMode !== 'ongoing' && (activity.scheduleMode !== 'dated' || dateDay(activity.dueDate) === null);
    case 'ongoing': return activity.status !== 'done' && activity.scheduleMode === 'ongoing';
    case 'not-started': case 'in-progress': case 'on-hold': case 'blocked': case 'done': return activity.status === params.get('insight');
    default: return true;
  }
}
export function buildExecutiveInsights(activities: Activity[], today: string, basis: InsightBasis, program = '', region = '') {
  const visible = countableActivities(activities, basis).filter(activity => (!program || activity.owningUnit === program) && (!region || activity.region === region));
  const open = visible.filter(activity => activity.status !== 'done');
  const attention = visible.filter(activity => activity.status === 'blocked' || overdueAge(activity, today) > 0)
    .sort((a, b) => Number(b.status === 'blocked') - Number(a.status === 'blocked') || Number(b.priority === 'high') - Number(a.priority === 'high') || overdueAge(b, today) - overdueAge(a, today) || a.title.localeCompare(b.title));
  const upcoming = visible.filter(activity => isUpcoming(activity, today)).sort((a, b) => a.dueDate!.localeCompare(b.dueDate!) || a.title.localeCompare(b.title));
  const programs = new Map<string, { id: string; label: string; values: Record<string, number>; attention: number }>();
  for (const activity of visible) {
    const row = programs.get(activity.owningUnit) || { id: activity.owningUnit, label: activity.owningUnit || 'Unassigned', values: {}, attention: 0 };
    row.values[activity.status] = (row.values[activity.status] || 0) + 1;
    if (activity.status === 'blocked' || overdueAge(activity, today) > 0) row.attention += 1;
    programs.set(activity.owningUnit, row);
  }
  const ages = [
    { id: '1-7', label: '1–7 days', value: 0, tone: 'warning' as const },
    { id: '8-30', label: '8–30 days', value: 0, tone: 'danger' as const },
    { id: '31+', label: '31+ days', value: 0, tone: 'danger' as const },
  ];
  for (const activity of visible) {
    const age = overdueAge(activity, today);
    if (age > 0) ages[age <= 7 ? 0 : age <= 30 ? 1 : 2].value += 1;
  }
  const countFilter = (insight: string) => visible.filter(activity => matchesInsight(activity, activities, new URLSearchParams({ insight }), today)).length;
  return { visible, open, attention, upcoming, programs: Array.from(programs.values()).sort((a, b) => b.attention - a.attention || a.label.localeCompare(b.label)), ages,
    undated: countFilter('undated'), ongoing: countFilter('ongoing'), done: visible.length - open.length };
}
